/**
 * Sanitizes input strings to comply with FHIR regex pattern: [ \r\n\t\S]+
 *
 * The FHIR pattern allows:
 * - Regular spaces, carriage returns, newlines, tabs
 * - Any non-whitespace characters
 *
 * But rejects:
 * - Non-standard Unicode whitespace (non-breaking spaces, zero-width spaces, etc.)
 * - Whitespace-only strings (no non-whitespace content)
 */

/**
 * Normalizes illegal whitespace characters to standard ones.
 * This should be called on every keystroke (onChange).
 *
 * @param value - The input string to sanitize
 * @returns Sanitized string with normalized whitespace
 */
export const normalizeWhitespace = (value: string): string => {
  if (!value) return value

  // Replace various Unicode whitespace characters with regular space
  // Non-breaking space (U+00A0), thin space (U+2009), zero-width space (U+200B),
  // en space (U+2002), em space (U+2003), figure space (U+2007), etc.
  let sanitized = value.replace(/[\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g, ' ')

  // Remove control characters (0x00-0x1F except tab, newline, carriage return)
  // and other illegal characters
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')

  return sanitized
}

/**
 * Checks if a string is whitespace-only and converts it to empty string.
 * This should be called on blur (onBlur) and also after normalization to prevent
 * storing whitespace-only values in form state.
 *
 * FHIR spec requires strings to contain at least one non-whitespace character.
 * This function detects all types of whitespace including spaces, tabs, newlines, etc.
 *
 * @param value - The input string to check
 * @returns Empty string if whitespace-only, otherwise the original value
 */
export const clearIfWhitespaceOnly = (value: string): string => {
  if (!value) return value

  // Check if the string contains only whitespace characters
  // This matches the FHIR requirement: strings MUST contain non-whitespace content
  // Using /\S/ to check for any non-whitespace character is more explicit than trim()
  // and handles all Unicode whitespace characters correctly
  return /\S/.test(value) ? value : ''
}

/**
 * Combined sanitization for both phases.
 * Use this when you need both normalization and whitespace-only checking.
 *
 * @param value - The input string to sanitize
 * @returns Fully sanitized string
 */
export const sanitizeInput = (value: string): string => {
  const normalized = normalizeWhitespace(value)
  return clearIfWhitespaceOnly(normalized)
}
